import { useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { useThemeStore } from '@/store/themeStore';
import { Toaster } from '@/components/ui/toaster';
import AuthGuard from '@/auth/AuthGuard';
import LoginPage from '@/pages/LoginPage';
import Dashboard from '@/pages/Dashboard';
import RepairRequests from '@/pages/RepairRequests';
import RepairRequestDetail from '@/pages/RepairRequestDetail';
import Equipment from '@/pages/Equipment';
import PMTracking from '@/pages/PMTracking';
import PMAdmin from '@/pages/PMAdmin';
import Calendar from '@/pages/Calendar';
import OrderedParts from '@/pages/OrderedParts';
import ThirdPartyRepair from '@/pages/ThirdPartyRepair';
import Admin from '@/pages/Admin';
import PlatformAdmin from '@/pages/PlatformAdmin';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import Layout from '@/components/layout/Layout';

const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/platform',
    element: <PlatformAdmin />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms',
    element: <TermsOfService />,
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        path: 'dashboard',
        element: <Dashboard />,
      },
      {
        path: 'repair-requests',
        element: (
          <AuthGuard blockedRoles={['accounting', 'ext_vendor']}>
            <RepairRequests />
          </AuthGuard>
        ),
      },
      {
        path: 'repair-requests/:id',
        element: (
          <AuthGuard blockedRoles={['accounting', 'ext_vendor']}>
            <RepairRequestDetail />
          </AuthGuard>
        ),
      },
      {
        path: 'equipment',
        element: (
          <AuthGuard blockedRoles={['submitter']}>
            <Equipment />
          </AuthGuard>
        ),
      },
      {
        path: 'pm-tracking',
        element: (
          <AuthGuard blockedRoles={['submitter', 'ext_vendor', 'accounting']}>
            <PMTracking />
          </AuthGuard>
        ),
      },
      {
        path: 'pm-admin',
        element: (
          <AuthGuard requiredRoles={['pm_admin', 'super_admin']}>
            <PMAdmin />
          </AuthGuard>
        ),
      },
      {
        path: 'calendar',
        element: (
          <AuthGuard blockedRoles={['submitter', 'ext_vendor', 'accounting']}>
            <Calendar />
          </AuthGuard>
        ),
      },
      {
        path: 'ordered-parts',
        element: (
          <AuthGuard requiredRoles={['repair_tech', 'accounting', 'super_admin', 'pm_admin', 'ext_vendor']}>
            <OrderedParts />
          </AuthGuard>
        ),
      },
      {
        path: 'third-party-repair',
        element: (
          <AuthGuard requiredRoles={['repair_tech', 'super_admin', 'pm_admin']}>
            <ThirdPartyRepair />
          </AuthGuard>
        ),
      },
      {
        path: 'admin',
        element: (
          <AuthGuard requiredRoles={['super_admin']}>
            <Admin />
          </AuthGuard>
        ),
      },
    ],
  },
]);

export default function App() {
  const { initTheme } = useThemeStore();

  useEffect(() => {
    initTheme();
  }, [initTheme]);

  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
    </>
  );
}
